import { useAppManager, useAppManagerDispatch, useClassicyDateTime } from "classicy";
import {
	type FC,
	type ReactNode,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import {
	type AlertItem,
	type AvailableSources,
	type ChatBuddy,
	type ChatMessage,
	type ChatStateReason,
	type FlightPosition,
	MediaStreamContext,
	type MediaItem,
	type PagerItem,
	type RoomCommand,
	type UsenetItem,
	type WeatherForecast,
	type WeatherObservation,
	type WsClockMessage,
	type WsHeartbeatAckMessage,
} from "./MediaStreamContext";
import { STREAM_URL } from "../../lib/endpoints";
import { trackAck } from "./ackTracking";
import { decodeWireMessage } from "./wireCodec";
import { drainDue, partitionByDue } from "./revealBuffer";
import { keepInstantItem, keepMediaItem } from "./retention";
import { shouldSeek } from "./seekDetection";
import { virtualUtcMs } from "./virtualClock";
import { playlistIdFromSearch } from "../Playlist/loadPlaylist";
import { usePlaylist } from "../Playlist/PlaylistContext";
import { playlistAppMeta } from "../Playlist/playlistApps";
import { setDateTimeFromUtc } from "../../Applications/TimeMachine/setVirtualClock";
import { mergeLatestPerStation } from "./weatherMerge";
import { reconnectDelayMs } from "./reconnectBackoff";
import {
	applyBodyFrame,
	emptyBodyState,
	type BodyFrame,
} from "./bodyCache";

// Merge incoming items into a prior list, de-duplicating by id (last write wins).
function mergeById<T extends { id: number }>(prev: T[], incoming: T[]): T[] {
	const byId = new Map(prev.map((i) => [i.id, i]));
	for (const item of incoming) byId.set(item.id, item);
	return Array.from(byId.values());
}

const HEARTBEAT_INTERVAL_MS = 30_000;

// Forced clock mode: corrections smaller than this are ignored (the local
// clock is close enough); larger ones snap to master via the sanctioned
// setDateTimeFromUtc seam. Kept well under SEEK_THRESHOLD_MS so routine
// corrections never clear buffers — only real operator jumps do.
export const FORCED_DRIFT_THRESHOLD_MS = 2_000;

// Heading-seed lookback: how many trailing minutes of flight positions to fetch
// (via a small flights_history request) whenever the flights channel (re)starts
// at an instant — subscribe, seek, reconnect. Flights are sampled per minute, so
// 3 minutes guarantees at least one sample OLDER than the snapshot's for every
// airborne flight, which is what the Flight Tracker needs to derive a heading
// for its planes immediately instead of pointing them north for the first
// minute. Kept deliberately small: a 30-minute loop-style window would be
// ~20k rows on every open/seek for data the seeding path throws away.
const FLIGHTS_SEED_LOOKBACK_MINUTES = 3;

interface WsItemsMessage {
	type: "items" | "init_ack" | "seek_ack";
	time?: string;
	items: MediaItem[];
}

interface WsPagerMessage {
	type: "pager";
	pager: PagerItem[];
}

// mp3 and news frames reuse the items field but carry a distinct type.
interface WsMp3Message {
	type: "mp3";
	items: MediaItem[];
}

// The complete mp3 back-catalogue up to the snapshot instant, sent with each mp3
// snapshot. Replaces client history state wholesale; may be empty (items omitted).
interface WsMp3HistoryMessage {
	type: "mp3_history";
	items?: MediaItem[];
}

interface WsNewsMessage {
	type: "news";
	items: MediaItem[];
}

// alerts ride their own `alerts` field (like usenet/flights), not items. Alerts
// are not time-pruned once due — a modal persists until the extension dismisses
// it (Task 8) — so the tick effect merges due alerts without a retention filter.
interface WsAlertsMessage {
	type: "alerts";
	alerts: AlertItem[];
}

// A live teacher command for the joined room. Relayed cross-pod by the streamer,
// so it arrives regardless of which replica this client's socket landed on.
interface WsRoomCommandMessage {
	type: "room_command";
	action: "jump" | "focus" | "message" | "lock" | "reload";
	time?: string;
	app?: string;
	message?: string;
	target?: "clock";
	on?: boolean;
}

// usenet messages ride their own field (not items) and carry per-message newsgroup.
interface WsUsenetMessage {
	type: "usenet";
	usenet: UsenetItem[];
}

interface WsUsenetBodyMessage {
	type: "usenet_body";
	id: number;
	body?: string;
	message?: string;
}

interface WsNewsBodyMessage {
	type: "news_body";
	id: number;
	body?: string;
	message?: string;
}

interface WsSourcesMessage {
	type: "sources";
	sources: AvailableSources;
}

// flight positions ride their own field (like usenet), not items.
interface WsFlightsMessage {
	type: "flights";
	flights: FlightPosition[];
}

// Chunked reply to a flights_history request. id echoes the request generation;
// the done frame (possibly empty) marks the window complete.
interface WsFlightsHistoryMessage {
	type: "flights_history";
	id?: number;
	flights?: FlightPosition[];
	done?: boolean;
}

// Weather observations ride their own `weather` field (like flights). The
// same-typed frame also carries `weather_forecasts` for products newly issued
// in-window, but Task 0 only exposes forecasts via the explicit request/reply
// round-trip (weatherForecastByZone) — the pushed list is not surfaced here.
interface WsWeatherMessage {
	type: "weather";
	time?: string;
	weather?: WeatherObservation[];
	weather_forecasts?: WeatherForecast[];
}

// Reply to a weather_forecast request. id echoes the request generation; an
// empty/omitted weather_forecasts is an explicit "no product for this zone"
// answer, distinct from a request that's still in flight.
interface WsWeatherForecastMessage {
	type: "weather_forecast";
	id?: number;
	weather_forecasts?: WeatherForecast[];
}

// The buddy roster, sent wholesale on subscribe/reconnect and whenever it
// changes. buddies rides omitempty on a struct shared with the 1 Hz items hot
// path, so an empty roster arrives with the field absent, not `[]`.
interface WsChatRosterMessage {
	type: "chat_roster";
	buddies?: WireBuddy[];
}

/**
 * A roster entry EXACTLY as the wire sends it.
 *
 * `chat_roster` is the one chat frame that names this value `id` — it carries
 * `model.Buddy`, whose json tag is `id` (websocket-protocol.md's chat_roster
 * table) — while `chat_presence`, `chat_typing` and `chat_message` all call the
 * same value `profile`. Declaring the frame as `ChatBuddy[]` and passing it
 * straight through therefore produced a roster whose every entry had
 * `profile: undefined`, and `buddies.find((b) => b.profile === selected)`
 * matched the FIRST buddy for every selection: clicking any buddy opened
 * Danny, Get Info always showed Danny, and chat_presence never matched anyone
 * so nobody ever went offline.
 *
 * Kept as a distinct type rather than widening ChatBuddy, so the wire's shape
 * and the app's shape cannot drift into each other again unnoticed.
 */
interface WireBuddy {
	id: number;
	screen_name: string;
	display_name?: string;
	avatar?: string;
	online?: boolean;
	profile_text?: string;
}

// Whether/why chat is usable right now for this client.
interface WsChatStateMessage {
	type: "chat_state";
	enabled?: boolean;
	reason?: ChatStateReason;
}

// A buddy's online flag flipped; applied to the existing roster entry.
interface WsChatPresenceMessage {
	type: "chat_presence";
	profile: number;
	online?: boolean;
}

// The server accepted a generation job for this buddy — the typing indicator
// stays up until the matching chat_message lands (no client-side timer).
interface WsChatTypingMessage {
	type: "chat_typing";
	profile: number;
}

// One chat line, in or out. Its arrival is also what clears chatTypingProfile.
interface WsChatMessageFrame {
	type: "chat_message";
	profile: number;
	direction: "in" | "out";
	body: string;
	time?: string;
	kind?: string;
	message_id?: number;
}

// The server confirming a chat_clear landed. `cleared` is the row count and is
// informational only — arrival alone empties the transcript, so a zero (an
// already-empty history, which msgpack omits) is still a success.
interface WsChatClearedMessage {
	type: "chat_cleared";
	cleared?: number;
}

type WsIncomingMessage =
	| WsItemsMessage
	| WsPagerMessage
	| WsMp3Message
	| WsMp3HistoryMessage
	| WsNewsMessage
	| WsAlertsMessage
	| WsUsenetMessage
	| WsUsenetBodyMessage
	| WsNewsBodyMessage
	| WsSourcesMessage
	| WsFlightsMessage
	| WsFlightsHistoryMessage
	| WsWeatherMessage
	| WsWeatherForecastMessage
	| WsChatRosterMessage
	| WsChatStateMessage
	| WsChatPresenceMessage
	| WsChatTypingMessage
	| WsChatMessageFrame
	| WsChatClearedMessage
	| WsClockMessage
	| WsHeartbeatAckMessage
	| { type: string };

interface MediaStreamProviderProps {
	children: ReactNode;
}

export const MediaStreamProvider: FC<MediaStreamProviderProps> = ({
	children,
}) => {
	const { localDate, dateTime, tzOffset, setDateTime, paused } = useClassicyDateTime({ tick: true });
	// setDateTime is a fresh closure every render; the long-lived WebSocket
	// onmessage handler (below) must always call the latest one, so it reads
	// through a ref rather than closing over the destructured value directly.
	const setDateTimeRef = useRef(setDateTime);
	useEffect(() => {
		setDateTimeRef.current = setDateTime;
	}, [setDateTime]);
	// Playlist availability predicate. Defaults to constant-true when no
	// PlaylistProvider is mounted (tests) or no playlist is active; while a
	// playlist runs, its identity changes each tick so windows open/close live.
	// The ref mirror is for the long-lived WebSocket onmessage closure, which
	// applies due items immediately and must not go stale (or force reconnects
	// by joining the socket effect's dependency list).
	const { isItemAvailable } = usePlaylist();
	const isItemAvailableRef = useRef(isItemAvailable);
	isItemAvailableRef.current = isItemAvailable;

	const [items, setItems] = useState<MediaItem[]>([]);
	const [pagerItems, setPagerItems] = useState<PagerItem[]>([]);
	const [mp3Items, setMp3Items] = useState<MediaItem[]>([]);
	const [mp3History, setMp3History] = useState<MediaItem[]>([]);
	const [newsItems, setNewsItems] = useState<MediaItem[]>([]);
	const [alertItems, setAlertItems] = useState<AlertItem[]>([]);
	const [usenetItems, setUsenetItems] = useState<UsenetItem[]>([]);
	const [flightPositions, setFlightPositions] = useState<FlightPosition[]>([]);
	const [flightsHistory, setFlightsHistory] = useState<FlightPosition[]>([]);
	const [flightsHistoryDone, setFlightsHistoryDone] = useState(false);
	// Short heading-seed lookback (see FLIGHTS_SEED_LOOKBACK_MINUTES): the
	// previous minute-buckets around the subscribe/seek instant, used by the
	// Flight Tracker to give single-sample flights a heading immediately.
	const [flightsSeed, setFlightsSeed] = useState<FlightPosition[]>([]);
	// Latest observation per station (see weatherMerge.ts). Not a plain list —
	// stations report at most hourly, so the UI wants "current reading per
	// station", not an accumulating array.
	const [weatherObservations, setWeatherObservations] = useState<
		Record<string, WeatherObservation>
	>({});
	// Forecast products fetched on demand, keyed by zone. null is a confirmed
	// "no product" answer, distinct from a key that was never requested.
	const [weatherForecastByZone, setWeatherForecastByZone] = useState<
		Record<string, WeatherForecast | null>
	>({});
	const [chatBuddies, setChatBuddies] = useState<ChatBuddy[]>([]);
	const [chatEnabled, setChatEnabled] = useState(false);
	// not_signed_in until the server says otherwise: assuming a working chat before
	// the first chat_state would flash an enabled UI at someone who cannot use it.
	const [chatReason, setChatReason] = useState<ChatStateReason>("not_signed_in");
	const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
	const [chatTypingProfile, setChatTypingProfile] = useState<number | null>(null);
	const [usenetBodyState, setUsenetBodyState] = useState(emptyBodyState);
	// Ids with a usenet_body request sent but not yet answered — prevents duplicate
	// fetches when a window re-renders before its body arrives.
	const usenetBodyInflight = useRef(new Set<number>());
	const [newsBodyState, setNewsBodyState] = useState(emptyBodyState);
	// Ids with a news_body request sent but not yet answered — prevents duplicate
	// requests when a detail window re-renders before its reply lands.
	const newsBodyInflight = useRef(new Set<number>());
	const [sources, setSources] = useState<AvailableSources>({ video: [], audio: [], pager: [], usenet: [] });
	const [connected, setConnected] = useState(false);

	// Per-app format subscriptions. null = wants all formats.
	const formatSubscriptions = useRef(new Map<string, string[] | null>());

	// Ref-counted channel subscribers. The server only delivers a channel's
	// items while at least one app is subscribed.
	const pagerSubscribers = useRef(new Set<string>());
	const mp3Subscribers = useRef(new Set<string>());
	const newsSubscribers = useRef(new Set<string>());
	const alertSubscribers = useRef(new Set<string>());
	const usenetSubscribers = useRef(new Set<string>());
	const flightsSubscribers = useRef(new Set<string>());
	// Anonymous radar traffic (RDR-… ids, #263): its own opt-in channel so the
	// dense extra payload is paid only while the map's "Other traffic" toggle
	// is on. Frames merge into the same position pipeline — the id prefix is
	// the discriminator everywhere downstream.
	const flightsAnonSubscribers = useRef(new Set<string>());
	const weatherSubscribers = useRef(new Set<string>());
	const chatSubscribers = useRef(new Set<string>());
	// Active loop-history request: window wanted (null = loop off). Loop-history
	// and heading-seed requests share the flights_history wire type, so they draw
	// ids from ONE counter (flightsReqGen) and each remembers its own active id
	// (0 = none) — the id echo then routes each reply chunk to the right consumer
	// and discards chunks of superseded requests (frames from request N can still
	// arrive after request N+1 goes out).
	const flightsHistoryMinutes = useRef<30 | 90 | null>(null);
	const flightsReqGen = useRef(0);
	const flightsLoopReqId = useRef(0);
	const flightsAnonLoopReqId = useRef(0);
	const flightsAnonSeedReqId = useRef(0);
	const flightsSeedReqId = useRef(0);
	// The single active forecast request (null = none pending) and a generation
	// id echoed by the server, mirroring flightsHistoryGen: a reply whose id
	// doesn't match the current generation is from a superseded request and is
	// dropped. Starts at 0 so the very first request sent carries id 1.
	const weatherForecastZone = useRef<string | null>(null);
	const weatherForecastGen = useRef(0);
	// Marks the NEXT non-empty weather frame as the subscribe/init/seek
	// snapshot (a full one-row-per-station picture at the current instant),
	// which must overwrite a station's entry outright — including with an
	// OLDER reading than what's held after a backward seek — rather than
	// going through mergeLatestPerStation's keep-newest rule (which exists to
	// protect ordinary forward-window refills from a late/reordered frame
	// regressing a station, not to reject a legitimate resync). Set whenever a
	// fresh snapshot is expected (subscribe, seek, reconnect-resubscribe) and
	// cleared once a due batch has been applied.
	const weatherSnapshotPending = useRef(true);
	// The newsgroup(s) the client is currently viewing — resent on reconnect.
	const usenetGroups = useRef<string[]>([]);

	// Reveal buffers: not-yet-due items from a windowed frame, keyed by id. The
	// per-second effect promotes each into live state when the virtual clock
	// reaches its start_date — this is what preserves forward-only pacing.
	const mediaBuffer = useRef(new Map<number, MediaItem>());
	const pagerBuffer = useRef(new Map<number, PagerItem>());
	const mp3Buffer = useRef(new Map<number, MediaItem>());
	const newsBuffer = useRef(new Map<number, MediaItem>());
	const alertBuffer = useRef(new Map<number, AlertItem>());
	const usenetBuffer = useRef(new Map<number, UsenetItem>());
	const flightsBuffer = useRef(new Map<number, FlightPosition>());
	const weatherBuffer = useRef(new Map<number, WeatherObservation>());

	const addItems = useCallback((incoming: MediaItem[]) => {
		setItems((prev) => {
			const byId = new Map(prev.map((i) => [i.id, i]));
			for (const item of incoming) byId.set(item.id, item);
			return Array.from(byId.values());
		});
	}, []);

	const wsRef = useRef<WebSocket | null>(null);

	// Bumping this re-runs the WebSocket effect, which is what actually performs a
	// reconnect: the cleanup closes the dead socket and the fresh pass builds a new
	// one, so `onopen`'s subscription replay is reached by exactly one code path
	// whether the connection is the first or the fifth.
	//
	// The streamer is redeployed on every merge to main and each rollout drops
	// every socket. Until this existed, `onclose` only flipped `connected` to
	// false: nothing constructed a second socket, so that replay could only ever
	// run at mount, and every later send() silently no-oped against a CLOSED
	// socket (see `send`). That is why a Flight Tracker that had gone empty could
	// not be recovered by seeking — the `seek` frame was swallowed too — and only
	// a page reload brought flights back.
	const [connectGen, setConnectGen] = useState(0);
	// Consecutive failed attempts, reset by a successful open. Drives the backoff.
	const reconnectAttempt = useRef(0);
	const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

	// Live mirror of the clock's paused state for the WebSocket effect's onopen,
	// which must assert pause on a connection opened while already paused.
	// Reading `paused` there directly would go stale, and adding it to that
	// effect's dependency array would tear down and rebuild the socket on every
	// pause — a reconnect storm from a button press.
	const pausedRef = useRef(paused);
	pausedRef.current = paused;

	// What the server was last told on THIS connection. null means nothing has
	// been sent yet, which is also the correct state for a fresh socket: `init`
	// resets Session.paused to false, so onopen re-establishes the truth.
	const pauseSentRef = useRef<boolean | null>(null);

	// Forward pause/resume to the streamer. Without this the server never learns
	// the clock stopped: Session.paused stays false, chat.Available never returns
	// "paused", and the IM Buddies composer stays open against a frozen clock.
	useEffect(() => {
		const ws = wsRef.current;
		if (!ws || ws.readyState !== WebSocket.OPEN) return;
		if (pauseSentRef.current === paused) return;
		// Say nothing about the default unpaused state: a `resume` for a session
		// that was never paused is meaningless traffic. onopen owns the initial
		// assertion.
		if (pauseSentRef.current === null && !paused) {
			pauseSentRef.current = false;
			return;
		}
		ws.send(JSON.stringify({ type: paused ? "pause" : "resume" }));
		pauseSentRef.current = paused;
	}, [paused]);

	// Always-current virtual *UTC* instant (ms) for use inside WS callbacks and
	// intervals. localDate is the tz-shifted display clock; the stream lives in
	// UTC (item start_dates, the backend, seek, calcSeekSeconds), so we strip the
	// offset back off. Mismatching these is what kept short-lived radio/news items
	// trapped in the reveal buffer — see virtualClock.ts.
	const utcMsRef = useRef(virtualUtcMs(localDate, tzOffset));
	const prevDateTimeRef = useRef(dateTime);

	useEffect(() => {
		utcMsRef.current = virtualUtcMs(localDate, tzOffset);
	}, [localDate, tzOffset]);

	// Forced clock mode: true while the server is driving the clock (Time
	// Machine locked server-side). clockForcedRef mirrors the state for the
	// long-lived socket closure, which needs the current value synchronously
	// (state updates are not visible until the next render).
	const [clockForced, setClockForced] = useState(false);
	// Latest live teacher command for the joined room; see RoomCommand.
	const [roomCommand, setRoomCommand] = useState<RoomCommand | null>(null);
	const clockForcedRef = useRef(false);

	// Snap the local clock to `iso` via the sanctioned setDateTimeFromUtc seam,
	// but only when it has drifted more than FORCED_DRIFT_THRESHOLD_MS from the
	// master time — small corrections are ignored so routine heartbeats don't
	// perpetually nudge the clock (and trip the seek-detection effect).
	const applyForcedTime = useCallback((iso: string) => {
		const masterMs = new Date(iso).getTime();
		if (Number.isNaN(masterMs)) return;
		if (Math.abs(masterMs - utcMsRef.current) > FORCED_DRIFT_THRESHOLD_MS) {
			setDateTimeFromUtc(setDateTimeRef.current, iso);
		}
	}, []);

	const setForced = useCallback((forced: boolean) => {
		if (clockForcedRef.current === forced) return;
		clockForcedRef.current = forced;
		setClockForced(forced);
	}, []);

	// --- Forced-clock UI enforcement ----------------------------------------
	// Reactive watcher, not action interception — same rationale as
	// PlaylistProvider's disabledApps sweep: watching `open` covers every
	// entry point including desktop-icon opens that never emit ClassicyAppOpen.
	const enforcementDispatch = useAppManagerDispatch();
	const timeMachineOpen = useAppManager(
		(s) => s.System.Manager.Applications.apps["TimeMachine.app"]?.open,
	);

	useEffect(() => {
		enforcementDispatch({
			type: clockForced ? "ClassicyManagerDateTimeLock" : "ClassicyManagerDateTimeUnlock",
		});
	}, [clockForced, enforcementDispatch]);

	useEffect(() => {
		if (!clockForced || !timeMachineOpen) return;
		enforcementDispatch({
			type: "ClassicyAppClose",
			app: { id: "TimeMachine.app", ...playlistAppMeta("TimeMachine.app") },
		});
		enforcementDispatch({
			type: "ClassicyDesktopShowErrorDialog",
			title: "Time Machine",
			message: "The clock is currently controlled by the broadcast operator.",
		});
	}, [clockForced, timeMachineOpen, enforcementDispatch]);

	const send = useCallback((msg: object) => {
		const ws = wsRef.current;
		if (ws?.readyState === WebSocket.OPEN) {
			ws.send(JSON.stringify(msg));
		}
	}, []);

	const sendFormatFilter = useCallback(() => {
		// The server refills the media window under the new whitelist; drop buffered
		// media selected under the old filter so it can't surface.
		mediaBuffer.current.clear();
		const subs = formatSubscriptions.current;
		// If no subscriptions or any app wants all formats → no filter
		if (subs.size === 0 || [...subs.values()].some((f) => f === null)) {
			send({ type: "filter", formats: null });
			return;
		}
		const all = new Set<string>();
		for (const formats of subs.values()) {
			if (formats) for (const f of formats) all.add(f);
		}
		send({ type: "filter", formats: [...all] });
	}, [send]);

	const subscribeFormats = useCallback(
		(appId: string, formats: string[] | null) => {
			formatSubscriptions.current.set(appId, formats);
			sendFormatFilter();
		},
		[sendFormatFilter],
	);

	const unsubscribeFormats = useCallback(
		(appId: string) => {
			formatSubscriptions.current.delete(appId);
			sendFormatFilter();
		},
		[sendFormatFilter],
	);

	const subscribePager = useCallback(
		(appId: string) => {
			const wasEmpty = pagerSubscribers.current.size === 0;
			pagerSubscribers.current.add(appId);
			if (wasEmpty) send({ type: "subscribe", channel: "pager" });
		},
		[send],
	);

	const unsubscribePager = useCallback(
		(appId: string) => {
			pagerSubscribers.current.delete(appId);
			if (pagerSubscribers.current.size === 0) {
				send({ type: "unsubscribe", channel: "pager" });
				setPagerItems([]);
				pagerBuffer.current.clear();
			}
		},
		[send],
	);

	const subscribeMp3 = useCallback(
		(appId: string) => {
			const wasEmpty = mp3Subscribers.current.size === 0;
			mp3Subscribers.current.add(appId);
			if (wasEmpty) send({ type: "subscribe", channel: "mp3" });
		},
		[send],
	);

	const unsubscribeMp3 = useCallback(
		(appId: string) => {
			mp3Subscribers.current.delete(appId);
			if (mp3Subscribers.current.size === 0) {
				send({ type: "unsubscribe", channel: "mp3" });
				setMp3Items([]);
				setMp3History([]);
				mp3Buffer.current.clear();
			}
		},
		[send],
	);

	const getUpcomingMp3Items = useCallback(
		(): MediaItem[] => Array.from(mp3Buffer.current.values()),
		[],
	);

	const subscribeNews = useCallback(
		(appId: string) => {
			const wasEmpty = newsSubscribers.current.size === 0;
			newsSubscribers.current.add(appId);
			if (wasEmpty) send({ type: "subscribe", channel: "news" });
		},
		[send],
	);

	const unsubscribeNews = useCallback(
		(appId: string) => {
			newsSubscribers.current.delete(appId);
			if (newsSubscribers.current.size === 0) {
				send({ type: "unsubscribe", channel: "news" });
				setNewsItems([]);
				newsBuffer.current.clear();
			}
		},
		[send],
	);

	const subscribeAlerts = useCallback(
		(appId: string) => {
			const wasEmpty = alertSubscribers.current.size === 0;
			alertSubscribers.current.add(appId);
			if (wasEmpty) send({ type: "subscribe", channel: "alerts" });
		},
		[send],
	);

	const unsubscribeAlerts = useCallback(
		(appId: string) => {
			alertSubscribers.current.delete(appId);
			if (alertSubscribers.current.size === 0) {
				send({ type: "unsubscribe", channel: "alerts" });
				setAlertItems([]);
				alertBuffer.current.clear();
			}
		},
		[send],
	);

	const subscribeUsenet = useCallback(
		(appId: string) => {
			const wasEmpty = usenetSubscribers.current.size === 0;
			usenetSubscribers.current.add(appId);
			if (wasEmpty) send({ type: "subscribe", channel: "usenet" });
		},
		[send],
	);

	const unsubscribeUsenet = useCallback(
		(appId: string) => {
			usenetSubscribers.current.delete(appId);
			if (usenetSubscribers.current.size === 0) {
				send({ type: "unsubscribe", channel: "usenet" });
				usenetGroups.current = [];
				setUsenetItems([]);
				usenetBuffer.current.clear();
			}
		},
		[send],
	);

	// (Re-)issue the heading-seed lookback for the current instant: fresh id,
	// reset the accumulated rows, ask again. No-op while nothing is subscribed —
	// the seed exists only to serve the flights channel's motion rendering.
	const sendFlightsSeedRequest = useCallback(() => {
		if (flightsSubscribers.current.size === 0) return;
		flightsReqGen.current += 1;
		flightsSeedReqId.current = flightsReqGen.current;
		setFlightsSeed([]);
		send({
			type: "flights_history",
			minutes: FLIGHTS_SEED_LOOKBACK_MINUTES,
			id: flightsSeedReqId.current,
		});
		if (flightsAnonSubscribers.current.size > 0) {
			flightsReqGen.current += 1;
			flightsAnonSeedReqId.current = flightsReqGen.current;
			send({
				type: "flights_history",
				channel: "flights-anon",
				minutes: FLIGHTS_SEED_LOOKBACK_MINUTES,
				id: flightsAnonSeedReqId.current,
			});
		}
	}, [send]);

	const subscribeFlights = useCallback(
		(appId: string) => {
			const wasEmpty = flightsSubscribers.current.size === 0;
			flightsSubscribers.current.add(appId);
			if (wasEmpty) {
				send({ type: "subscribe", channel: "flights" });
				sendFlightsSeedRequest();
			}
		},
		[send, sendFlightsSeedRequest],
	);

	const unsubscribeFlights = useCallback(
		(appId: string) => {
			flightsSubscribers.current.delete(appId);
			if (flightsSubscribers.current.size === 0) {
				send({ type: "unsubscribe", channel: "flights" });
				setFlightPositions([]);
				flightsBuffer.current.clear();
				// Loop history and the heading seed are flights-channel features;
				// drop them with the channel (id 0 orphans any in-flight chunks).
				flightsHistoryMinutes.current = null;
				flightsLoopReqId.current = 0;
				setFlightsHistory([]);
				setFlightsHistoryDone(false);
				flightsSeedReqId.current = 0;
				setFlightsSeed([]);
			}
		},
		[send],
	);

	// (Re-)issue the active history request: fresh id, reset the accumulated
	// window, ask again. No-op while loop mode is off.
	const subscribeFlightsAnon = useCallback(
		(appId: string) => {
			const wasEmpty = flightsAnonSubscribers.current.size === 0;
			flightsAnonSubscribers.current.add(appId);
			if (wasEmpty) {
				send({ type: "subscribe", channel: "flights-anon" });
				// Join any active loop window / heading seed mid-flight.
				if (flightsHistoryMinutes.current !== null) {
					flightsReqGen.current += 1;
					flightsAnonLoopReqId.current = flightsReqGen.current;
					send({
						type: "flights_history",
						channel: "flights-anon",
						minutes: flightsHistoryMinutes.current,
						id: flightsAnonLoopReqId.current,
					});
				}
				flightsReqGen.current += 1;
				flightsAnonSeedReqId.current = flightsReqGen.current;
				send({
					type: "flights_history",
					channel: "flights-anon",
					minutes: FLIGHTS_SEED_LOOKBACK_MINUTES,
					id: flightsAnonSeedReqId.current,
				});
			}
		},
		[send],
	);

	const unsubscribeFlightsAnon = useCallback(
		(appId: string) => {
			flightsAnonSubscribers.current.delete(appId);
			if (flightsAnonSubscribers.current.size === 0) {
				send({ type: "unsubscribe", channel: "flights-anon" });
				// Purge only the anonymous corpus — named flights stay live.
				setFlightPositions((prev) => prev.filter((p) => !p.flight.startsWith("RDR-")));
				for (const [id, p] of flightsBuffer.current) {
					if (p.flight.startsWith("RDR-")) flightsBuffer.current.delete(id);
				}
				setFlightsHistory((prev) => prev.filter((p) => !p.flight.startsWith("RDR-")));
				setFlightsSeed((prev) => prev.filter((p) => !p.flight.startsWith("RDR-")));
				flightsAnonLoopReqId.current = 0;
				flightsAnonSeedReqId.current = 0;
			}
		},
		[send],
	);

	const sendFlightsHistoryRequest = useCallback(() => {
		const minutes = flightsHistoryMinutes.current;
		if (minutes === null) return;
		flightsReqGen.current += 1;
		flightsLoopReqId.current = flightsReqGen.current;
		setFlightsHistory([]);
		setFlightsHistoryDone(false);
		send({ type: "flights_history", minutes, id: flightsLoopReqId.current });
		if (flightsAnonSubscribers.current.size > 0) {
			flightsReqGen.current += 1;
			flightsAnonLoopReqId.current = flightsReqGen.current;
			send({
				type: "flights_history",
				channel: "flights-anon",
				minutes,
				id: flightsAnonLoopReqId.current,
			});
		}
	}, [send]);

	const requestFlightsHistory = useCallback(
		(minutes: 30 | 90) => {
			flightsHistoryMinutes.current = minutes;
			sendFlightsHistoryRequest();
		},
		[sendFlightsHistoryRequest],
	);

	const clearFlightsHistory = useCallback(() => {
		flightsHistoryMinutes.current = null;
		flightsLoopReqId.current = 0; // orphan any in-flight chunks
		setFlightsHistory([]);
		setFlightsHistoryDone(false);
	}, []);

	const subscribeWeather = useCallback(
		(appId: string) => {
			const wasEmpty = weatherSubscribers.current.size === 0;
			weatherSubscribers.current.add(appId);
			if (wasEmpty) {
				weatherSnapshotPending.current = true;
				send({ type: "subscribe", channel: "weather" });
			}
		},
		[send],
	);

	const unsubscribeWeather = useCallback(
		(appId: string) => {
			weatherSubscribers.current.delete(appId);
			if (weatherSubscribers.current.size === 0) {
				send({ type: "unsubscribe", channel: "weather" });
				setWeatherObservations({});
				weatherBuffer.current.clear();
				// A forecast request is meaningless without an active subscription;
				// drop it and orphan any reply still in flight.
				weatherForecastZone.current = null;
				weatherForecastGen.current += 1;
				setWeatherForecastByZone({});
			}
		},
		[send],
	);

	// (Re-)issue the active forecast request: bump the generation, ask again.
	// No-op while no zone is pending. Mirrors sendFlightsHistoryRequest.
	const sendWeatherForecastRequest = useCallback(() => {
		const zone = weatherForecastZone.current;
		if (zone === null) return;
		weatherForecastGen.current += 1;
		send({ type: "weather_forecast", zone, id: weatherForecastGen.current });
	}, [send]);

	const requestWeatherForecast = useCallback(
		(zone: string) => {
			weatherForecastZone.current = zone;
			sendWeatherForecastRequest();
		},
		[sendWeatherForecastRequest],
	);

	const subscribeChat = useCallback(
		(appId: string) => {
			const wasEmpty = chatSubscribers.current.size === 0;
			chatSubscribers.current.add(appId);
			if (wasEmpty) send({ type: "subscribe", channel: "chat" });
		},
		[send],
	);

	const unsubscribeChat = useCallback(
		(appId: string) => {
			chatSubscribers.current.delete(appId);
			if (chatSubscribers.current.size === 0) send({ type: "unsubscribe", channel: "chat" });
		},
		[send],
	);

	const sendChat = useCallback(
		(profile: number, body: string) => {
			send({ type: "chat_send", profile, body });
		},
		[send],
	);

	// Ask the server to soft-delete this user's whole chat history. Deliberately
	// does NOT touch chatMessages here — the transcript is emptied when the
	// server's chat_cleared frame arrives (see the handler), so an unauthorized
	// or failed clear leaves what is on screen exactly where it was.
	//
	// No arguments: the server scopes the clear to the session's authenticated
	// user, so there is nothing here that could aim it at anyone else.
	const clearChatData = useCallback(() => {
		send({ type: "chat_clear" });
	}, [send]);

	// Requesting history for a profile also DROPS that profile's local echoes,
	// in the same call, deliberately: a replay is authoritative for the
	// conversation it covers, and it carries the persisted copies of the very
	// lines the echoes stand in for.
	//
	// chat.HistoryDetailed (internal/chat/store.go) has no direction filter, so
	// the student's own direction:"in" turns come back with real message_ids —
	// while an echo's message_id 0 is exempt from dedupe by design (it is the
	// server's "not persisted" marker, not an identity). Echo and persisted
	// copy therefore cannot dedupe against each other, and every history path
	// that does not clear the transcript first would render the line twice.
	// Only the backward-seek path clears; a forward seek with a window open and
	// an ordinary open/reopen do not.
	//
	// The drop lives INSIDE the request rather than in a separate exported
	// helper so a future call site cannot forget it: "history requested" and
	// "echoes for that profile dropped" are one operation.
	//
	// The tradeoff, taken knowingly: the line is briefly absent until the
	// replay lands (one round trip), and permanently absent if the replay never
	// arrives. Dropping when the replay LANDS instead would close that gap but
	// opens a worse hole — an echo created between the request and the reply
	// has no persisted copy in that reply, so it would be dropped and lost for
	// good. A visible gap beats silently eating a line the student just typed,
	// and this matches how the backward-seek clear already behaves.
	const requestChatHistory = useCallback(
		(profile: number, before: string, limit: number) => {
			setChatMessages((prev) =>
				prev.filter((m) => !(m.message_id === 0 && m.profile === profile)),
			);
			send({ type: "chat_history", profile, before, limit });
		},
		[send],
	);

	// The student's own turn, rendered locally. The server never echoes it: it
	// persists the inbound line (internal/session/session.go persistInbound)
	// and every live chat_message frame is direction "out" — a direction "in"
	// line only ever comes back through a chat_history replay. Without this the
	// student types, the field clears, and their words vanish.
	//
	// It goes into the SAME chatMessages array the server frames land in, so
	// insertion order alone keeps the transcript right and there is no second
	// list to merge. The caller stamps message_id 0 ("not persisted"), which
	// the chat app's dedupe already treats as a non-identity.
	const appendLocalChatMessage = useCallback((message: ChatMessage) => {
		setChatMessages((prev) => [...prev, message]);
	}, []);

	// Set the newsgroup(s) being viewed. The server resends a backlog for the new
	// group(s), so the current items + buffer are cleared to avoid mixing groups.
	const setUsenetGroups = useCallback(
		(groups: string[]) => {
			usenetGroups.current = groups;
			setUsenetItems([]);
			usenetBuffer.current.clear();
			send({ type: "usenet_filter", newsgroups: groups });
		},
		[send],
	);

	// Request the next page of older messages for a group; the server replies on the
	// usenet frame (all older items are ≤ clock, so they merge straight in).
	const requestUsenetOlder = useCallback(
		(newsgroup: string, before: string) => {
			send({ type: "usenet_more", newsgroups: [newsgroup], before });
		},
		[send],
	);

	// Fetch one message body on demand. Skips ids already cached, already errored,
	// or already in flight; bodies are immutable so a cached one is never refetched.
	const requestUsenetBody = useCallback(
		(id: number) => {
			if (
				id in usenetBodyState.bodies ||
				id in usenetBodyState.errors ||
				usenetBodyInflight.current.has(id)
			) {
				return;
			}
			usenetBodyInflight.current.add(id);
			send({ type: "usenet_body", id });
		},
		[send, usenetBodyState],
	);

	// Fetch one article's body on demand. Snapshot rows carry no content (the
	// backlog would be ~7.7 MB with bodies), so the detail window asks for it when
	// it opens. Cached bodies, cached errors, and in-flight ids are all skipped, so
	// callers can fire this on every render.
	const requestNewsBody = useCallback(
		(id: number) => {
			if (
				id in newsBodyState.bodies ||
				id in newsBodyState.errors ||
				newsBodyInflight.current.has(id)
			) {
				return;
			}
			newsBodyInflight.current.add(id);
			send({ type: "news_body", id });
		},
		[send, newsBodyState],
	);

	// On every second tick: reveal buffered items the clock has now reached, then
	// prune expired ones. drainDue mutates the buffer (removing promoted entries);
	// the merged-then-filtered state both surfaces newly-due items and drops
	// expired ones in a single pass, keyed by the same `now`.
	useEffect(() => {
		const now = virtualUtcMs(localDate, tzOffset);

		const dueMedia = drainDue(mediaBuffer.current, now);
		const dueMp3 = drainDue(mp3Buffer.current, now);
		const dueNews = drainDue(newsBuffer.current, now);
		const dueAlerts = drainDue(alertBuffer.current, now);
		const duePager = drainDue(pagerBuffer.current, now);
		const dueUsenet = drainDue(usenetBuffer.current, now);
		const dueFlights = drainDue(flightsBuffer.current, now);
		const dueWeather = drainDue(weatherBuffer.current, now);

		// Playlist availability windows gate items here — at the single choke
		// point every app's catalog flows through — so out-of-window items are
		// hidden uniformly. With no playlist active the predicate is a stable
		// constant-true and this is byte-identical to the ungated behavior.
		setItems((prev) =>
			mergeById(prev, dueMedia).filter(
				(item) => keepMediaItem(item, now) && isItemAvailable("tv", item.source ?? ""),
			),
		);
		// mp3 items are durational audio — same retention rules as media items.
		setMp3Items((prev) =>
			mergeById(prev, dueMp3).filter(
				(item) => keepMediaItem(item, now) && isItemAvailable("radio", item.source ?? ""),
			),
		);
		// News is NOT time-pruned: the News app shows the full back catalogue from
		// the 9/9 epoch onward, so an article stays once revealed. The playlist
		// availability gate stays — that is availability, not date gating. A
		// backward seek clears the list wholesale instead (see the seek effect).
		setNewsItems((prev) =>
			mergeById(prev, dueNews).filter((item) => isItemAvailable("news", String(item.id))),
		);
		// Alerts are not time-pruned: a modal persists until the extension
		// dismisses it (Task 8), so there is no keepMediaItem/isItemAvailable
		// filter here — only merge newly-due alerts in.
		if (dueAlerts.length > 0) setAlertItems((prev) => mergeById(prev, dueAlerts));
		// Pager items are always instant — retain by start_date.
		setPagerItems((prev) => mergeById(prev, duePager).filter((p) => keepInstantItem(p, now)));
		// Usenet messages are not time-pruned: a reader keeps browsing the group's
		// backlog. They are cleared only on group change, unsubscribe, or seek.
		if (dueUsenet.length > 0) setUsenetItems((prev) => mergeById(prev, dueUsenet));
		// Flight positions are instant per-minute samples — pager-style retention.
		// pager/usenet/weather are NOT playlist-gated (restrict scope is
		// tv/radio/news/flights; a teacher removes those apps wholesale instead).
		setFlightPositions((prev) =>
			mergeById(prev, dueFlights).filter(
				(p) => keepInstantItem(p, now) && isItemAvailable("flights", p.flight),
			),
		);
		// Weather observations are latest-per-station, not time-pruned — a
		// station's last reading stays visible however old it gets.
		if (dueWeather.length > 0)
			setWeatherObservations((prev) => mergeLatestPerStation(prev, dueWeather));
	}, [localDate, tzOffset, isItemAvailable]);

	// Detect manual time changes and send seek; ignore tick-driven minute
	// boundaries. Forward and backward use different thresholds (see
	// shouldSeek): a forward jump must clear a tick's worth to count, but any
	// real backward move is a seek — the clock never rewinds on its own, and a
	// small rewind that isn't re-fetched leaves already-revealed items dropped
	// with nothing to re-surface them.
	useEffect(() => {
		const prevMs = new Date(prevDateTimeRef.current).getTime();
		const nowMs = new Date(dateTime).getTime();

		if (shouldSeek(prevMs, nowMs)) {
			// The server sends a fresh window for the new instant; drop buffered
			// items from the old timeline so they never surface.
			mediaBuffer.current.clear();
			pagerBuffer.current.clear();
			mp3Buffer.current.clear();
			newsBuffer.current.clear();
			// News is no longer time-pruned, so nothing else would drop articles
			// dated after a backward-seek target. The snapshot for the new instant
			// repopulates the full back catalogue up to it.
			setNewsItems([]);
			// A cached body must not survive past its own article. If it did, a
			// detail window left open across a backward seek would show article
			// text from an instant BEFORE that article was published — the
			// newsItems clear above only blanks the title/date, not the body
			// cache, so this reset is load-bearing, not a redundant flush. The
			// body simply re-fetches (via requestNewsBody) if the article is
			// still valid at the new instant. Also drop in-flight markers so a
			// news_body reply from the old timeline can't land as a stale body.
			setNewsBodyState(emptyBodyState);
			newsBodyInflight.current.clear();
			alertBuffer.current.clear();
			setAlertItems([]);
			// The server resends a fresh usenet backlog for the active group(s) at the
			// new instant; drop the old-timeline messages so they don't linger.
			usenetBuffer.current.clear();
			setUsenetItems([]);
			// Chat, unlike everything else here, is cleared on a BACKWARD seek
			// only. Chat history turns arrive as ordinary chat_message frames
			// appended to the same flat array, so after a rewind the student
			// would otherwise still see every post-seek line with the refetched
			// older lines below them — a character remembering what has not
			// happened yet, the exact anachronism the backend's tier system
			// exists to prevent. A forward seek has no such problem (nothing on
			// screen postdates the new instant) and clearing there would blank
			// a conversation mid-sentence. This also drops the student's own
			// local echoes, which live in the same array by design. The chat
			// app re-requests a page per open conversation (and on reopening
			// one) to refill.
			if (nowMs < prevMs) setChatMessages([]);
			flightsBuffer.current.clear();
			// Per-station weather state is intentionally NOT cleared here — the
			// post-seek "weather" snapshot frame (covering every station at the
			// new instant) replaces each station's entry as it arrives, so the
			// UI doesn't flash empty on seek. Mark it pending so that frame (not
			// the ordinary keep-newest merge) is what performs the replacement.
			weatherBuffer.current.clear();
			weatherSnapshotPending.current = true;
			// Forecast products, unlike per-station observations, have no
			// post-seek push that would replace them (they're request/reply
			// only) — a kept entry would show the old timeline's product at the
			// new clock, an anachronism. Clear them all; the re-fired request
			// below repopulates the active zone (the product for the new
			// instant, or an explicit null), and its generation bump orphans
			// any in-flight pre-seek reply via the id-echo guard.
			setWeatherForecastByZone({});
			send({ type: "seek", time: new Date(dateTime).toISOString() });
			// The old timeline's loop history is meaningless at the new instant.
			sendFlightsHistoryRequest();
			// The motion buffer rebuilds from single samples after a seek, so the
			// north-pointing first minute would recur — re-seed at the new instant.
			sendFlightsSeedRequest();
			// Re-ask for the last-requested zone's forecast at the new clock
			// (no-op when none is pending) — mirrors the reconnect path.
			sendWeatherForecastRequest();
		}

		prevDateTimeRef.current = dateTime;
	}, [dateTime, send, sendFlightsHistoryRequest, sendFlightsSeedRequest, sendWeatherForecastRequest]);

	// Once the socket is OPEN, re-request the window for the current instant.
	// The active video channels are long-running stitched HLS streams (one row in
	// tv_channels per channel, started days ago) so they are delivered ONLY by the
	// init/seek snapshot — CurrentItems matches items overlapping the instant,
	// whereas the per-second refill is keyed by start_date over a forward window
	// and never re-sends an already-running channel. On the initial connect the
	// onopen `init` can run before the virtual clock has settled on its seeded
	// instant, and the clock-settle seek above is dropped while the socket is still
	// CONNECTING (send() no-ops unless OPEN) — leaving video empty with no recovery
	// until a manual date change. Issuing a seek for the freshest clock value once
	// connected guarantees the active channels arrive on every (re)connect.
	// biome-ignore lint/correctness/useExhaustiveDependencies: utcMsRef is a stable ref read for its latest value
	useEffect(() => {
		if (!connected) return;
		send({ type: "seek", time: new Date(utcMsRef.current).toISOString() });
	}, [connected, send]);

	// WebSocket lifecycle: connect once, heartbeat inside onopen
	useEffect(() => {
		let active = true;
		let heartbeatId: ReturnType<typeof setInterval>;
		const ws = new WebSocket(STREAM_URL);
		// Must be set synchronously at construction (not in onopen) so the first
		// binary frame arrives as an ArrayBuffer, not a Blob.
		ws.binaryType = "arraybuffer";
		wsRef.current = ws;

		ws.onopen = () => {
			// StrictMode cleanup may have run before the socket finished connecting.
			if (!active) {
				ws.close();
				return;
			}
			setConnected(true);
			// A connection that opened is a connection that worked; the next outage
			// starts its backoff from scratch rather than inheriting this one's.
			reconnectAttempt.current = 0;
			ws.send(
				JSON.stringify({
					type: "init",
					time: new Date(utcMsRef.current).toISOString(),
				}),
			);
			// Rejoin the teacher-controlled room. Same reason as the
			// subscriptions below: room membership lives on the session, so a
			// reconnect (or a failover onto another pod) starts with none.
			const room = playlistIdFromSearch(window.location.search);
			if (room) ws.send(JSON.stringify({ type: "join_room", room }));
			// Re-establish channel subscriptions after a reconnect — the server
			// does not remember subscriptions across connections.
			if (pagerSubscribers.current.size > 0) {
				ws.send(JSON.stringify({ type: "subscribe", channel: "pager" }));
			}
			if (mp3Subscribers.current.size > 0) {
				ws.send(JSON.stringify({ type: "subscribe", channel: "mp3" }));
			}
			if (newsSubscribers.current.size > 0) {
				ws.send(JSON.stringify({ type: "subscribe", channel: "news" }));
			}
			if (alertSubscribers.current.size > 0) {
				ws.send(JSON.stringify({ type: "subscribe", channel: "alerts" }));
			}
			if (usenetSubscribers.current.size > 0) {
				ws.send(JSON.stringify({ type: "subscribe", channel: "usenet" }));
				// Restore the viewed-group filter so the backlog comes back after reconnect.
				if (usenetGroups.current.length > 0) {
					ws.send(JSON.stringify({ type: "usenet_filter", newsgroups: usenetGroups.current }));
				}
			}
			if (flightsSubscribers.current.size > 0) {
				ws.send(JSON.stringify({ type: "subscribe", channel: "flights" }));
				if (flightsAnonSubscribers.current.size > 0) {
					ws.send(JSON.stringify({ type: "subscribe", channel: "flights-anon" }));
				}
				// Loop mode survives a reconnect: re-seed its window at the fresh clock.
				sendFlightsHistoryRequest();
				// The subscribe-time heading seed was lost with the old connection.
				sendFlightsSeedRequest();
			}
			if (weatherSubscribers.current.size > 0) {
				weatherSnapshotPending.current = true;
				ws.send(JSON.stringify({ type: "subscribe", channel: "weather" }));
				// A pending forecast request survives a reconnect: re-fire it so the
				// answer isn't silently lost to the dropped connection.
				sendWeatherForecastRequest();
			}
			if (chatSubscribers.current.size > 0) {
				ws.send(JSON.stringify({ type: "subscribe", channel: "chat" }));
			}
			// `init` above resets Session.paused to false (protocol doc, §init), so
			// a connection opened while the clock is paused would hand the server a
			// running clock and re-enable the chat composer. This is not just
			// reconnect hardening: `paused` is persisted in classicyDesktopState, so
			// pausing and reloading the page lands here every time. Same reason the
			// subscriptions above are replayed.
			if (pausedRef.current) {
				ws.send(JSON.stringify({ type: "pause" }));
			}
			pauseSentRef.current = pausedRef.current;
			// Body requests do not survive a reconnect; clear in-flight markers so
			// any open message window re-requests on its next render.
			usenetBodyInflight.current.clear();
			newsBodyInflight.current.clear();
			heartbeatId = setInterval(() => {
				if (ws.readyState === WebSocket.OPEN) {
					ws.send(
						JSON.stringify({
							type: "heartbeat",
							time: new Date(utcMsRef.current).toISOString(),
						}),
					);
				}
			}, HEARTBEAT_INTERVAL_MS);
		};

		ws.onmessage = (event: MessageEvent<ArrayBuffer>) => {
			if (!active) return;
			let msg: WsIncomingMessage;
			try {
				msg = decodeWireMessage<WsIncomingMessage>(event.data);
			} catch {
				return;
			}

			// Each frame is now a forward *window*: items already due surface
			// immediately; future items wait in the reveal buffer until the clock
			// reaches their start_date (preserving forward-only pacing). init_ack /
			// seek_ack snapshots are all active-now, so they land entirely in `due`.
			const now = utcMsRef.current;

			// Time-independent source lists for filters — sent once at init. Replace
			// wholesale (the server always sends the complete set).
			if (msg.type === "sources") {
				const incoming = (msg as WsSourcesMessage).sources;
				if (incoming) {
					setSources({
						video: incoming.video ?? [],
						audio: incoming.audio ?? [],
						pager: incoming.pager ?? [],
						usenet: incoming.usenet ?? [],
					});
				}
				return;
			}

			if (msg.type === "pager") {
				const incomingPager = (msg as WsPagerMessage).pager;
				if (!incomingPager || incomingPager.length === 0) return;
				const { due, future } = partitionByDue(incomingPager, now);
				for (const p of future) pagerBuffer.current.set(p.id, p);
				const fresh = due.filter((p) => keepInstantItem(p, now));
				if (fresh.length > 0) setPagerItems((prev) => mergeById(prev, fresh));
				return;
			}

			if (msg.type === "mp3") {
				const incomingMp3 = (msg as WsMp3Message).items;
				if (!incomingMp3 || incomingMp3.length === 0) return;
				const { due, future } = partitionByDue(incomingMp3, now);
				for (const item of future) mp3Buffer.current.set(item.id, item);
				const fresh = due.filter(
					(item) =>
						keepMediaItem(item, now) &&
						isItemAvailableRef.current("radio", item.source ?? ""),
				);
				if (fresh.length > 0) setMp3Items((prev) => mergeById(prev, fresh));
				return;
			}

			if (msg.type === "mp3_history") {
				// The full back-catalogue up to the snapshot instant. Replace wholesale
				// (each frame is complete, and an empty one clears after a backward
				// seek); skip the reveal buffer and retention — history is already past.
				setMp3History((msg as WsMp3HistoryMessage).items ?? []);
				return;
			}

			if (msg.type === "news") {
				const incomingNews = (msg as WsNewsMessage).items;
				if (!incomingNews || incomingNews.length === 0) return;
				const { due, future } = partitionByDue(incomingNews, now);
				for (const item of future) newsBuffer.current.set(item.id, item);
				// No retention filter here: a backlog frame is almost entirely
				// articles older than any retention window, and dropping them on
				// arrival is exactly what kept the News app to a 10-minute memory.
				const fresh = due.filter((item) =>
					isItemAvailableRef.current("news", String(item.id)),
				);
				if (fresh.length > 0) setNewsItems((prev) => mergeById(prev, fresh));
				return;
			}

			if (msg.type === "alerts") {
				const incomingAlerts = (msg as WsAlertsMessage).alerts;
				if (!incomingAlerts || incomingAlerts.length === 0) return;
				const { due, future } = partitionByDue(incomingAlerts, now);
				for (const item of future) alertBuffer.current.set(item.id, item);
				// Alerts are not time-pruned or playlist-gated — a modal persists
				// until the extension dismisses it (Task 8).
				if (due.length > 0) setAlertItems((prev) => mergeById(prev, due));
				return;
			}

			if (msg.type === "usenet") {
				const incomingUsenet = (msg as WsUsenetMessage).usenet;
				if (!incomingUsenet || incomingUsenet.length === 0) return;
				const { due, future } = partitionByDue(incomingUsenet, now);
				for (const item of future) usenetBuffer.current.set(item.id, item);
				// Backlog + due items surface immediately; no time-prune (see tick effect).
				if (due.length > 0) setUsenetItems((prev) => mergeById(prev, due));
				return;
			}

			if (msg.type === "usenet_body") {
				const frame = msg as WsUsenetBodyMessage;
				usenetBodyInflight.current.delete(frame.id);
				setUsenetBodyState((prev) =>
					applyBodyFrame(prev, frame as BodyFrame),
				);
				return;
			}

			if (msg.type === "news_body") {
				const frame = msg as WsNewsBodyMessage;
				newsBodyInflight.current.delete(frame.id);
				setNewsBodyState((prev) => applyBodyFrame(prev, frame as BodyFrame));
				return;
			}

			if (msg.type === "flights_history") {
				const hist = msg as WsFlightsHistoryMessage;
				// Loop-history and heading-seed replies share this wire type; the
				// echoed request id says which consumer (if either) each chunk is
				// for. An active id is never 0 (the counter starts at 1), so the
				// 0-means-inactive sentinel can't match a real frame.
				if (hist.id !== 0
					&& (hist.id === flightsSeedReqId.current
						|| hist.id === flightsAnonSeedReqId.current)) {
					const incoming = hist.flights ?? [];
					if (incoming.length > 0)
						setFlightsSeed((prev) => [...prev, ...incoming]);
					return;
				}
				if (hist.id === 0
					|| (hist.id !== flightsLoopReqId.current
						&& hist.id !== flightsAnonLoopReqId.current)) return; // superseded
				const incoming = hist.flights ?? [];
				if (incoming.length > 0)
					setFlightsHistory((prev) => [...prev, ...incoming]);
				// The named-corpus request drives the loop's ready flag; the anon
				// chunks enrich the window as they arrive.
				if (hist.done && hist.id === flightsLoopReqId.current) setFlightsHistoryDone(true);
				return;
			}

			if (msg.type === "flights" || msg.type === "flights_anon") {
				const incomingFlights = (msg as WsFlightsMessage).flights;
				if (!incomingFlights || incomingFlights.length === 0) return;
				const { due, future } = partitionByDue(incomingFlights, now);
				for (const p of future) flightsBuffer.current.set(p.id, p);
				const fresh = due.filter(
					(p) =>
						keepInstantItem(p, now) &&
						isItemAvailableRef.current("flights", p.flight),
				);
				if (fresh.length > 0)
					setFlightPositions((prev) => mergeById(prev, fresh));
				return;
			}

			if (msg.type === "chat_roster") {
				// buddies rides omitempty on a struct shared with the 1 Hz items path, so an
				// empty roster arrives as an absent field rather than [].
				//
				// `id` -> `profile` is the load-bearing part: see WireBuddy. Every
				// other chat frame keys this value `profile`, and the rest of the
				// app does too, so the rename happens once, here at the boundary.
				setChatBuddies(
					((msg as WsChatRosterMessage).buddies ?? []).map((b) => ({
						profile: b.id,
						screen_name: b.screen_name,
						display_name: b.display_name ?? "",
						avatar: b.avatar ?? "",
						online: b.online === true,
						profile_text: b.profile_text,
					})),
				);
				return;
			}

			if (msg.type === "chat_state") {
				const m = msg as WsChatStateMessage;
				setChatEnabled(m.enabled === true);
				setChatReason(m.reason ?? "not_signed_in");
				return;
			}

			if (msg.type === "chat_presence") {
				const m = msg as WsChatPresenceMessage;
				setChatBuddies((prev) =>
					prev.map((b) => (b.profile === m.profile ? { ...b, online: m.online === true } : b)));
				return;
			}

			if (msg.type === "chat_typing") {
				setChatTypingProfile((msg as WsChatTypingMessage).profile);
				return;
			}

			if (msg.type === "chat_message") {
				const m = msg as WsChatMessageFrame;
				// The reply is what ends the typing indicator — not a timer. The backend
				// sends chat_typing on accepting the job and the message when it lands.
				setChatTypingProfile(null);
				setChatMessages((prev) => [
					...prev,
					{
						message_id: m.message_id ?? 0,
						profile: m.profile,
						direction: m.direction,
						body: m.body,
						time: m.time ?? "",
						kind: m.kind ?? "generated",
					},
				]);
				return;
			}

			// Emptying the transcript is driven by the server's confirmation, never
			// optimistically by the click: the rows are only hidden once the write
			// lands, and a failed clear replies with an `error` instead — so the
			// student never sees a blank conversation that a reconnect refills.
			//
			// Clearing every profile at once (rather than filtering by one) matches
			// what the server did: chat_clear has no profile field.
			if (msg.type === "chat_cleared") {
				setChatMessages([]);
				setChatTypingProfile(null);
				return;
			}

			if (msg.type === "weather") {
				const incomingWeather = (msg as WsWeatherMessage).weather;
				if (!incomingWeather || incomingWeather.length === 0) return;
				const { due, future } = partitionByDue(incomingWeather, now);
				for (const obs of future) weatherBuffer.current.set(obs.id, obs);
				if (due.length > 0) {
					if (weatherSnapshotPending.current) {
						// The subscribe/init/seek snapshot: one row per station at the
						// current instant. Overwrite outright rather than merging —
						// see weatherSnapshotPending's declaration for why.
						weatherSnapshotPending.current = false;
						setWeatherObservations((prev) => {
							const next = { ...prev };
							for (const reading of due) next[reading.station_id] = reading;
							return next;
						});
					} else {
						setWeatherObservations((prev) => mergeLatestPerStation(prev, due));
					}
				}
				return;
			}

			if (msg.type === "weather_forecast") {
				const reply = msg as WsWeatherForecastMessage;
				if (reply.id !== weatherForecastGen.current) return; // superseded request
				const zone = weatherForecastZone.current;
				if (zone === null) return;
				const forecasts = reply.weather_forecasts ?? [];
				setWeatherForecastByZone((prev) => ({
					...prev,
					// Explicit-empty reply → confirmed "no product", not "still pending".
					[zone]: forecasts.length > 0 ? forecasts[0] : null,
				}));
				return;
			}

			if (msg.type === "room_command") {
				const m = msg as WsRoomCommandMessage;
				// seq is what makes a repeated identical command observable —
				// a teacher may focus the same app twice on purpose.
				setRoomCommand((prev) => ({
					action: m.action,
					time: m.time,
					app: m.app,
					message: m.message,
					target: m.target,
					on: m.on,
					seq: (prev?.seq ?? 0) + 1,
				}));
				return;
			}

			if (msg.type === "clock") {
				const m = msg as WsClockMessage;
				setForced(m.active);
				if (m.active && m.time) applyForcedTime(m.time);
				return;
			}

			if (msg.type === "heartbeat_ack") {
				const m = msg as WsHeartbeatAckMessage;
				// master_time presence IS the forced signal — self-heals a
				// missed clock frame in either direction.
				setForced(typeof m.master_time === "string");
				if (m.master_time) applyForcedTime(m.master_time);
				return;
			}

			if (msg.type !== "items" && msg.type !== "init_ack" && msg.type !== "seek_ack") return;

			trackAck(msg.type, (msg as WsItemsMessage).time);

			const incoming = (msg as WsItemsMessage).items;
			if (incoming.length === 0) return;

			const { due, future } = partitionByDue(incoming, now);
			for (const item of future) mediaBuffer.current.set(item.id, item);
			const fresh = due.filter(
				(item) =>
					keepMediaItem(item, now) &&
					isItemAvailableRef.current("tv", item.source ?? ""),
			);
			if (fresh.length > 0) setItems((prev) => mergeById(prev, fresh));
		};

		ws.onclose = () => {
			if (!active) return;
			setConnected(false);
			clearInterval(heartbeatId);
			// Schedule the rebuild rather than doing it here: bumping connectGen
			// re-runs this effect, whose cleanup closes this socket properly before
			// the new one is made.
			const delay = reconnectDelayMs(reconnectAttempt.current);
			reconnectAttempt.current += 1;
			reconnectTimer.current = setTimeout(() => {
				reconnectTimer.current = null;
				setConnectGen((g) => g + 1);
			}, delay);
		};

		ws.onerror = () => {
			ws.close();
		};

		return () => {
			active = false;
			clearInterval(heartbeatId);
			// A pending retry must not outlive the provider, or it bumps state on an
			// unmounted tree and resurrects the socket after teardown.
			if (reconnectTimer.current !== null) {
				clearTimeout(reconnectTimer.current);
				reconnectTimer.current = null;
			}
			ws.onclose = null;
			// Calling close() on a CONNECTING socket logs a browser error.
			// Defer to onopen so it can close cleanly once the handshake finishes.
			if (ws.readyState === WebSocket.CONNECTING) {
				ws.onopen = () => ws.close();
			} else {
				ws.close();
			}
			wsRef.current = null;
		};
		// Re-runs only when connectGen changes (a scheduled reconnect); utcMsRef
		// carries the live value. sendFlightsHistoryRequest/sendFlightsSeedRequest/
		// sendWeatherForecastRequest/setForced/applyForcedTime are all stable (empty
		// or `send`-only deps), so listing them satisfies the lint without
		// re-running the effect.
	}, [
		connectGen,
		sendFlightsHistoryRequest,
		sendFlightsSeedRequest,
		sendWeatherForecastRequest,
		setForced,
		applyForcedTime,
	]);

	// mp3History and sources arrive as whole frames (not through the per-second
	// reveal tick), so the playlist gate is applied at read-out instead.
	const gatedMp3History = useMemo(
		() => mp3History.filter((i) => isItemAvailable("radio", i.source ?? "")),
		[mp3History, isItemAvailable],
	);
	const gatedSources = useMemo<AvailableSources>(
		() => ({
			...sources,
			video: sources.video.filter((s) => isItemAvailable("tv", s)),
			audio: sources.audio.filter((s) => isItemAvailable("radio", s)),
		}),
		[sources, isItemAvailable],
	);

	// Memoize the context value so consumers only re-render when specific data
	// changes — not on every provider render (which happens every clock tick and
	// on every classicy state update such as window-focus changes).
	const contextValue = useMemo(
		() => ({
			items,
			pagerItems,
			mp3Items,
			mp3History: gatedMp3History,
			newsItems,
			alertItems,
			usenetItems,
			usenetBodies: usenetBodyState.bodies,
			usenetBodyErrors: usenetBodyState.errors,
			requestUsenetBody,
			newsBodies: newsBodyState.bodies,
			newsBodyErrors: newsBodyState.errors,
			requestNewsBody,
			sources: gatedSources,
			connected,
			addItems,
			subscribeFormats,
			unsubscribeFormats,
			subscribePager,
			unsubscribePager,
			subscribeMp3,
			unsubscribeMp3,
			getUpcomingMp3Items,
			subscribeNews,
			unsubscribeNews,
			subscribeAlerts,
			unsubscribeAlerts,
			subscribeUsenet,
			unsubscribeUsenet,
			setUsenetGroups,
			requestUsenetOlder,
			flightPositions,
			subscribeFlights,
			unsubscribeFlights,
			subscribeFlightsAnon,
			unsubscribeFlightsAnon,
			flightsHistory,
			flightsHistoryDone,
			flightsSeed,
			requestFlightsHistory,
			clearFlightsHistory,
			weatherObservations,
			weatherForecastByZone,
			subscribeWeather,
			unsubscribeWeather,
			requestWeatherForecast,
			clockForced,
			roomCommand,
			chatBuddies,
			chatEnabled,
			chatReason,
			chatMessages,
			chatTypingProfile,
			subscribeChat,
			unsubscribeChat,
			sendChat,
			requestChatHistory,
			appendLocalChatMessage,
			clearChatData,
		}),
		[
			items,
			pagerItems,
			mp3Items,
			gatedMp3History,
			newsItems,
			alertItems,
			usenetItems,
			usenetBodyState,
			requestUsenetBody,
			newsBodyState,
			requestNewsBody,
			gatedSources,
			connected,
			addItems,
			subscribeFormats,
			unsubscribeFormats,
			subscribePager,
			unsubscribePager,
			subscribeMp3,
			unsubscribeMp3,
			getUpcomingMp3Items,
			subscribeNews,
			unsubscribeNews,
			subscribeAlerts,
			unsubscribeAlerts,
			subscribeUsenet,
			unsubscribeUsenet,
			setUsenetGroups,
			requestUsenetOlder,
			flightPositions,
			subscribeFlights,
			unsubscribeFlights,
			subscribeFlightsAnon,
			unsubscribeFlightsAnon,
			flightsHistory,
			flightsHistoryDone,
			flightsSeed,
			requestFlightsHistory,
			clearFlightsHistory,
			weatherObservations,
			weatherForecastByZone,
			subscribeWeather,
			unsubscribeWeather,
			requestWeatherForecast,
			clockForced,
			roomCommand,
			chatBuddies,
			chatEnabled,
			chatReason,
			chatMessages,
			chatTypingProfile,
			subscribeChat,
			unsubscribeChat,
			sendChat,
			requestChatHistory,
			appendLocalChatMessage,
			clearChatData,
		],
	);

	return (
		<MediaStreamContext.Provider value={contextValue}>
			{children}
		</MediaStreamContext.Provider>
	);
};
